/**
 * API contracts for the `bc-integration` module — zod schemas + inferred types shared by
 * apps/api, apps/web and (via generated types) apps/android.
 *
 * OWNERSHIP: the bc-integration module agent owns this file exclusively. It is
 * pre-created (and pre-exported from ./index.ts) by the scaffold so filling it
 * in never requires touching a shared file. Import from '@warehouse/shared'.
 *
 * Naming: every exported symbol is prefixed `erp`/`ERP_` (domain-neutral) or
 * `bc`/`BC_` (Business-Central-specific wire shapes) so the barrel in
 * ./index.ts can never collide with another module's contract.
 */
import { z } from 'zod';

import { listQueryParamsSchema, listResponseSchema, uuidSchema } from '../schemas/common.js';

// ---------------------------------------------------------------------------
// entities + cursors
// ---------------------------------------------------------------------------

/**
 * The BC entity sets we synchronise, in dependency order — a full resync run
 * top to bottom resolves every foreign key on the way (units before items,
 * items before item units).
 */
export const ERP_ENTITIES = [
  'unitsOfMeasure',
  'itemCategories',
  'items',
  'itemUnitsOfMeasure',
  'customers',
  'vendors',
  'salesOrders',
  'purchaseOrders',
  'productionOrders',
] as const;

export const erpEntitySchema = z.enum(ERP_ENTITIES);
export type ErpEntity = z.infer<typeof erpEntitySchema>;

/** Which WMS register an entity feeds — drives the dashboard grouping. */
export const ERP_ENTITY_TARGET: Readonly<Record<ErpEntity, string>> = {
  unitsOfMeasure: 'units',
  itemCategories: 'article_categories',
  items: 'articles',
  itemUnitsOfMeasure: 'article_units',
  customers: 'restaurants',
  vendors: 'suppliers',
  salesOrders: 'sales_orders',
  purchaseOrders: 'purchase_orders',
  productionOrders: 'production_orders',
};

/** `erp_refs.entity` values — the WMS side of an id mapping. */
export const ERP_REF_ENTITIES = [
  'unit',
  'article_category',
  'article',
  'article_unit',
  'restaurant',
  'supplier',
  'sales_order',
  'sales_order_line',
  'purchase_order',
  'purchase_order_line',
  'production_order',
] as const;
export const erpRefEntitySchema = z.enum(ERP_REF_ENTITIES);
export type ErpRefEntity = z.infer<typeof erpRefEntitySchema>;

// ---------------------------------------------------------------------------
// outbox / inbox
// ---------------------------------------------------------------------------

/**
 * `erp_outbox.kind` (DATA-MODEL "erp"). A kind is the contract between the
 * module that queued the message (`ctx.erp({ kind, payload })` inside its
 * command transaction) and the worker in this module that ships it to BC.
 */
export const ERP_OUTBOX_KINDS = [
  'so.create',
  'so.update',
  'so.cancel',
  'so.ship',
  'po.receipt',
  'prod.create',
  'prod.output',
  'inv.adjust',
] as const;
export const erpOutboxKindSchema = z.enum(ERP_OUTBOX_KINDS);
export type ErpOutboxKind = z.infer<typeof erpOutboxKindSchema>;

export const erpOutboxStatusSchema = z.enum(['pending', 'sent', 'failed', 'dead']);
export type ErpOutboxStatus = z.infer<typeof erpOutboxStatusSchema>;

export const erpInboxStatusSchema = z.enum(['pending', 'applied', 'skipped', 'failed']);
export type ErpInboxStatus = z.infer<typeof erpInboxStatusSchema>;

export const erpInboxSourceSchema = z.enum(['webhook', 'poll']);
export type ErpInboxSource = z.infer<typeof erpInboxSourceSchema>;

export const erpOutboxRowSchema = z.object({
  id: uuidSchema,
  kind: z.string(),
  payload: z.unknown(),
  status: erpOutboxStatusSchema,
  attempts: z.number().int().nonnegative(),
  nextAt: z.iso.datetime(),
  lastError: z.string().nullable(),
  sentAt: z.iso.datetime().nullable(),
  idempotencyKey: z.string().nullable(),
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
});
export type ErpOutboxRow = z.infer<typeof erpOutboxRowSchema>;

export const erpInboxRowSchema = z.object({
  id: uuidSchema,
  source: erpInboxSourceSchema,
  kind: z.string(),
  bcEntity: z.string(),
  payload: z.unknown(),
  status: erpInboxStatusSchema,
  error: z.string().nullable(),
  receivedAt: z.iso.datetime(),
  processedAt: z.iso.datetime().nullable(),
});
export type ErpInboxRow = z.infer<typeof erpInboxRowSchema>;

export const erpOutboxListResponseSchema = listResponseSchema(erpOutboxRowSchema);
export const erpInboxListResponseSchema = listResponseSchema(erpInboxRowSchema);

/** Both browsers accept the shared list contract; these are the declared keys. */
export const erpOutboxListQuerySchema = listQueryParamsSchema;
export const erpInboxListQuerySchema = listQueryParamsSchema;

// ---------------------------------------------------------------------------
// sync state + status dashboard
// ---------------------------------------------------------------------------

export const erpEntityStateSchema = z.object({
  entity: erpEntitySchema,
  /** WMS table the entity lands in (dashboard subtitle). */
  target: z.string(),
  /** BC `lastModifiedDateTime` delta cursor; null ⇒ never synced. */
  cursor: z.string().nullable(),
  lastFullSyncAt: z.iso.datetime().nullable(),
  lastError: z.string().nullable(),
  webhookSubId: z.string().nullable(),
  webhookExpiresAt: z.iso.datetime().nullable(),
  updatedAt: z.iso.datetime().nullable(),
  /** Rows waiting in `erp_inbox` for this entity. */
  pendingInbox: z.number().int().nonnegative(),
  failedInbox: z.number().int().nonnegative(),
});
export type ErpEntityState = z.infer<typeof erpEntityStateSchema>;

export const erpEnvironmentKindSchema = z.enum(['sandbox', 'production', 'unknown']);
export type ErpEnvironmentKind = z.infer<typeof erpEnvironmentKindSchema>;

export const erpStatusSchema = z.object({
  /** `bc` = BcAdapter, `null` = NullErpAdapter (WMS runs standalone). */
  mode: z.enum(['bc', 'null']),
  environment: z.string().nullable(),
  environmentKind: erpEnvironmentKindSchema,
  companyName: z.string().nullable(),
  companyId: z.string().nullable(),
  /** Last connectivity probe (token + company resolution). */
  connected: z.boolean(),
  connectionError: z.string().nullable(),
  productionSyncEnabled: z.boolean(),
  webhooksEnabled: z.boolean(),
  webhookNotificationUrl: z.string().nullable(),
  counts: z.object({
    outboxPending: z.number().int().nonnegative(),
    outboxFailed: z.number().int().nonnegative(),
    outboxDead: z.number().int().nonnegative(),
    inboxPending: z.number().int().nonnegative(),
    inboxFailed: z.number().int().nonnegative(),
  }),
  entities: z.array(erpEntityStateSchema),
});
export type ErpStatus = z.infer<typeof erpStatusSchema>;

export const erpSubscriptionSchema = z.object({
  entity: erpEntitySchema.nullable(),
  subscriptionId: z.string().nullable(),
  resource: z.string(),
  expiresAt: z.iso.datetime().nullable(),
  /** true when the subscription renews in less than the 3-day horizon. */
  expiringSoon: z.boolean(),
  registered: z.boolean(),
});
export type ErpSubscription = z.infer<typeof erpSubscriptionSchema>;

export const erpWebhookOverviewSchema = z.object({
  enabled: z.boolean(),
  notificationUrl: z.string().nullable(),
  renewHorizonHours: z.number().int().positive(),
  subscriptions: z.array(erpSubscriptionSchema),
});
export type ErpWebhookOverview = z.infer<typeof erpWebhookOverviewSchema>;

// ---------------------------------------------------------------------------
// commands (CommandBus types — usable from web AND POST /api/sync/commands)
// ---------------------------------------------------------------------------

export const ERP_COMMANDS = {
  resync: 'erp.resync',
  outboxRetry: 'erp.outboxRetry',
  outboxCancel: 'erp.outboxCancel',
  inboxRetry: 'erp.inboxRetry',
  webhooksRenew: 'erp.webhooksRenew',
} as const;
export type ErpCommandType = (typeof ERP_COMMANDS)[keyof typeof ERP_COMMANDS];

export const erpResyncPayloadSchema = z.object({
  entity: erpEntitySchema,
  /** `full` clears the cursor first — the manual "sync everything" button. */
  mode: z.enum(['delta', 'full']).default('delta'),
});
export type ErpResyncPayload = z.infer<typeof erpResyncPayloadSchema>;

export const erpResyncResultSchema = z.object({
  entity: erpEntitySchema,
  mode: z.enum(['delta', 'full']),
  fetched: z.number().int().nonnegative(),
  queued: z.number().int().nonnegative(),
  cursor: z.string().nullable(),
});
export type ErpResyncResult = z.infer<typeof erpResyncResultSchema>;

export const erpOutboxRetryPayloadSchema = z.object({ id: uuidSchema });
export type ErpOutboxRetryPayload = z.infer<typeof erpOutboxRetryPayloadSchema>;

export const erpOutboxCancelPayloadSchema = z.object({
  id: uuidSchema,
  reason: z.string().trim().max(500).optional(),
});
export type ErpOutboxCancelPayload = z.infer<typeof erpOutboxCancelPayloadSchema>;

export const erpInboxRetryPayloadSchema = z.object({ id: uuidSchema });
export type ErpInboxRetryPayload = z.infer<typeof erpInboxRetryPayloadSchema>;

export const erpWebhooksRenewPayloadSchema = z.object({}).default({});
export type ErpWebhooksRenewPayload = z.infer<typeof erpWebhooksRenewPayloadSchema>;

/** Envelope accepted by the admin mutation endpoints (commandId optional). */
export function erpCommandBodySchema<T extends z.ZodTypeAny>(payload: T) {
  return z.object({ commandId: uuidSchema.optional(), payload });
}

// ---------------------------------------------------------------------------
// outbox payloads (what other modules put on the wire via `ctx.erp(...)`)
// ---------------------------------------------------------------------------

/**
 * These schemas are the CONTRACT between the queuing module (M4 sales orders,
 * M3 receiving, M2 stocktaking, M6 production) and this module's workers. They
 * are validated when the worker picks the row up — a malformed payload dies in
 * the outbox instead of producing a half-written BC document.
 */
export const erpSalesOrderLinePayloadSchema = z.object({
  lineId: uuidSchema.optional(),
  articleNo: z.string().min(1),
  quantity: z.number().positive(),
  unitCode: z.string().min(1).optional(),
});

export const erpSalesOrderPayloadSchema = z.object({
  orderId: uuidSchema,
  orderNo: z.string().min(1),
  /** BC customer number; resolved from `restaurants.bc_customer_no`. */
  customerNo: z.string().min(1),
  orderDate: z.string().min(1).optional(),
  requestedDeliveryDate: z.string().min(1).optional(),
  lines: z.array(erpSalesOrderLinePayloadSchema).min(1),
});
export type ErpSalesOrderPayload = z.infer<typeof erpSalesOrderPayloadSchema>;

export const erpSalesOrderShipPayloadSchema = z.object({
  orderId: uuidSchema,
  orderNo: z.string().min(1),
  shipmentNo: z.string().optional(),
});
export type ErpSalesOrderShipPayload = z.infer<typeof erpSalesOrderShipPayloadSchema>;

export const erpSalesOrderCancelPayloadSchema = z.object({
  orderId: uuidSchema,
  orderNo: z.string().min(1),
});
export type ErpSalesOrderCancelPayload = z.infer<typeof erpSalesOrderCancelPayloadSchema>;

export const erpPurchaseReceiptPayloadSchema = z.object({
  receiptId: uuidSchema.optional(),
  purchaseOrderId: uuidSchema.optional(),
  poNo: z.string().min(1),
  lines: z
    .array(
      z.object({
        articleNo: z.string().min(1),
        quantity: z.number().nonnegative(),
        unitCode: z.string().optional(),
        lotNo: z.string().optional(),
      }),
    )
    .optional(),
});
export type ErpPurchaseReceiptPayload = z.infer<typeof erpPurchaseReceiptPayloadSchema>;

export const erpInventoryAdjustLineSchema = z.object({
  articleNo: z.string().min(1),
  /** Signed: positive = increase, negative = decrease. */
  quantity: z.number(),
  unitCode: z.string().optional(),
  locationCode: z.string().optional(),
  lotNo: z.string().optional(),
  reason: z.string().max(100).optional(),
});

export const erpInventoryAdjustPayloadSchema = z.object({
  reference: z.string().min(1),
  postingDate: z.string().optional(),
  lines: z.array(erpInventoryAdjustLineSchema).min(1),
});
export type ErpInventoryAdjustPayload = z.infer<typeof erpInventoryAdjustPayloadSchema>;

export const erpProductionOrderPayloadSchema = z.object({
  productionOrderId: uuidSchema,
  orderNo: z.string().min(1),
  articleNo: z.string().min(1),
  quantity: z.number().positive(),
  dueDate: z.string().optional(),
});
export type ErpProductionOrderPayload = z.infer<typeof erpProductionOrderPayloadSchema>;

export const erpProductionOutputPayloadSchema = z.object({
  productionOrderId: uuidSchema,
  orderNo: z.string().min(1),
  articleNo: z.string().min(1),
  outputQuantity: z.number().positive(),
  lotNo: z.string().optional(),
  postingDate: z.string().optional(),
  components: z
    .array(
      z.object({
        articleNo: z.string().min(1),
        quantity: z.number().positive(),
        lotNo: z.string().optional(),
      }),
    )
    .optional(),
});
export type ErpProductionOutputPayload = z.infer<typeof erpProductionOutputPayloadSchema>;

// ---------------------------------------------------------------------------
// BC webhook wire format
// ---------------------------------------------------------------------------

/**
 * A Business Central push notification. BC posts an array of these to the
 * registered `notificationUrl`; the first request of a subscription's life is
 * instead a handshake carrying `?validationToken=…` which must be echoed back
 * verbatim as `text/plain` within 5 seconds.
 */
export const bcWebhookNotificationSchema = z.object({
  subscriptionId: z.string().optional(),
  clientState: z.string().optional(),
  expirationDateTime: z.string().optional(),
  /** e.g. `api/v2.0/companies(<guid>)/items(<guid>)` */
  resource: z.string(),
  changeType: z.string().optional(),
  lastModifiedDateTime: z.string().optional(),
});
export type BcWebhookNotification = z.infer<typeof bcWebhookNotificationSchema>;

export const bcWebhookPayloadSchema = z.object({
  value: z.array(bcWebhookNotificationSchema).default([]),
});
export type BcWebhookPayload = z.infer<typeof bcWebhookPayloadSchema>;

export const bcWebhookAckSchema = z.object({
  accepted: z.number().int().nonnegative(),
  ignored: z.number().int().nonnegative(),
});
export type BcWebhookAck = z.infer<typeof bcWebhookAckSchema>;

/** `sandbox` is anything not named like a production environment. */
export function erpEnvironmentKind(environment: string | null | undefined): ErpEnvironmentKind {
  if (!environment) return 'unknown';
  return /sandbox|sbx|dev|test|uat|preview|qa/i.test(environment) ? 'sandbox' : 'production';
}
